import { nodeSize, pinPosition, sourceKey } from "../engine/registry";
export function bounds(project) {
  const xs = [0, 960],
    ys = [0, 340];
  project.components.forEach((c) => {
    const z = nodeSize(c);
    xs.push(c.x - 80, c.x + z.w + 32);
    ys.push(c.y - 30, c.y + z.h + 32);
  });
  project.connections.forEach((e) => {
    for (const p of [
      ...(e.waypoints || []),
      ...(e.terminal ? [e.terminal] : []),
    ]) {
      xs.push(p.x - 24, p.x + 24);
      ys.push(p.y - 24, p.y + 24);
    }
  });
  project.inputs.forEach(input => {
    const p = inputTerminal(project, input.id);
    if (p) { xs.push(p.x - Math.min(24, input.name.length) * 6.5 - 12); ys.push(p.y - 12); }
  });
  const x = Math.min(...xs),
    y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}
export function inputTerminal(project, id) {
  const e = project.connections.find(w => w.from.input === id);
  const c = project.components.find(c => c.id === e?.to.component);
  if (!c) return null;
  const end = pinPosition(c, e.to.pin, "in");
  return e.terminal || {x: end.x - 42, y: end.y};
}
export function endpoints(project, e) {
  const to = project.components.find((c) => c.id === e.to.component),
    from = project.components.find((c) => c.id === e.from.component);
  if (!to) return null;
  const end = pinPosition(to, e.to.pin, "in");
  return {
    start: from
      ? pinPosition(from, e.from.pin, "out")
      : (e.from.input && inputTerminal(project, e.from.input)) || e.terminal || { x: end.x - 42, y: end.y },
    end,
  };
}
export function manualPoints(project, e) {
  const ends = endpoints(project, e);
  if (!ends) return [];
  const raw = [
    ends.start,
    ...(e.waypoints || []).map((p) => ({ ...p })),
    ends.end,
  ];
  if (raw.length > 2) {
    const old = e.anchors || ends,
      first = raw[1],
      last = raw[raw.length - 2];
    if (first.x === old.start.x) first.x = ends.start.x;
    else first.y = ends.start.y;
    if (last.x === old.end.x) last.x = ends.end.x;
    else last.y = ends.end.y;
  }
  const points = [raw[0]];
  for (let i = 1; i < raw.length; i++) {
    const a = points[points.length - 1],
      b = raw[i];
    if (a.x !== b.x && a.y !== b.y) points.push({ x: a.x, y: b.y });
    if (a.x !== b.x || a.y !== b.y) points.push(b);
  }
  return compact(points);
}
export function bendSegment(points, index, position) {
  const out = points.map((p) => ({ ...p })),
    a = out[index],
    b = out[index + 1];
  if (!a || !b) return out;
  const horizontal = Math.abs(a.y - b.y) < 0.01,
    axis = horizontal ? "y" : "x";
  const shiftedA = { ...a, [axis]: position[axis] },
    shiftedB = { ...b, [axis]: position[axis] };
  // Endpoints stay attached; extra elbows make even the first/last segment bendable.
  if (index === 0) out.splice(1, 0, shiftedA);
  else out[index] = shiftedA;
  const j = index === 0 ? index + 2 : index + 1;
  if (index + 1 === points.length - 1) out.splice(j, 0, shiftedB);
  else out[j] = shiftedB;
  return out;
}
const compact = (points) =>
  points.filter(
    (p, i, a) =>
      !i ||
      i === a.length - 1 ||
      !(
        (a[i - 1].x === p.x && p.x === a[i + 1].x) ||
        (a[i - 1].y === p.y && p.y === a[i + 1].y)
      ),
  );
export function sourceStub(project, source) {
  const c = project.components.find((c) => c.id === source.component);
  if (!c) return null;
  const start = pinPosition(c, source.pin, "out");
  return { x: start.x + 9, y: start.y };
}
// Dots belong where a shared net actually branches, including manually bent paths.
export function wireJunctions(routes) {
  const groups = new Map();
  for(const route of routes){const key=sourceKey(route.e.from);if(!groups.has(key))groups.set(key,[]);groups.get(key).push(route);}
  const result=[];
  for(const group of groups.values()){
    if(group.length<2)continue;
    const candidates=new Map(group.flatMap(r=>r.points).map(p=>[`${p.x}:${p.y}`,p]));
    for(const p of candidates.values()){
      const dirs=new Set();
      for(const r of group)for(let i=1;i<r.points.length;i++){
        const a=r.points[i-1],b=r.points[i];
        if(a.x===b.x&&p.x===a.x&&p.y>=Math.min(a.y,b.y)&&p.y<=Math.max(a.y,b.y)){
          if(Math.min(a.y,b.y)<p.y)dirs.add('up');if(Math.max(a.y,b.y)>p.y)dirs.add('down');
        }
        if(a.y===b.y&&p.y===a.y&&p.x>=Math.min(a.x,b.x)&&p.x<=Math.max(a.x,b.x)){
          if(Math.min(a.x,b.x)<p.x)dirs.add('left');if(Math.max(a.x,b.x)>p.x)dirs.add('right');
        }
      }
      if(dirs.size>=3)result.push({point:p,source:group[0].e.from});
    }
  }
  return result;
}
export function wirePoints(project, e, occupied = []) {
  if (e.waypoints?.length) return manualPoints(project, e);
  const to = project.components.find((c) => c.id === e.to.component);
  if (!to) return [];
  const end = pinPosition(to, e.to.pin, "in"),
    from = project.components.find((c) => c.id === e.from.component);
  if (!from && !e.from.input) return [e.terminal || { x: end.x - 42, y: end.y }, end];
  const start = from ? pinPosition(from, e.from.pin, "out") : inputTerminal(project, e.from.input),
    s = from ? sourceStub(project, e.from) : {x: start.x + 8, y: start.y},
    t = { x: end.x - 9, y: end.y };
  const obstacles = project.components.map((c) => {
    const z = nodeSize(c);
    return { x: c.x - 5, y: c.y - 5, r: c.x + z.w + 5, b: c.y + z.h + 5 };
  });
  const blocked = (a, b) =>
    obstacles.some((o) =>
      a.x === b.x
        ? a.x > o.x &&
          a.x < o.r &&
          Math.max(a.y, b.y) > o.y &&
          Math.min(a.y, b.y) < o.b
        : a.y > o.y &&
          a.y < o.b &&
          Math.max(a.x, b.x) > o.x &&
          Math.min(a.x, b.x) < o.r,
    );
  const keyOfNet = sourceKey(e.from);
  const overlaps = (a, b) =>
    occupied.some((segment) => {
      if (segment.key === keyOfNet) return false;
      const { a: c, b: d } = segment;
      return a.x === b.x && c.x === d.x && a.x === c.x
        ? Math.min(Math.max(a.y, b.y), Math.max(c.y, d.y)) -
            Math.max(Math.min(a.y, b.y), Math.min(c.y, d.y)) >
            0
        : a.y === b.y && c.y === d.y && a.y === c.y
          ? Math.min(Math.max(a.x, b.x), Math.max(c.x, d.x)) -
              Math.max(Math.min(a.x, b.x), Math.min(c.x, d.x)) >
            0
          : false;
    });
  const mid = { x: t.x, y: s.y };
  if (
    !e.routeOffset &&
    !blocked(s, mid) &&
    !blocked(mid, t) &&
    !overlaps(s, mid) &&
    !overlaps(mid, t)
  )
    return compact([start, s, mid, t, end]);
  const xs = [
    ...new Set([
      s.x,
      t.x,
      s.x + 8,
      t.x - 8,
      ...obstacles.flatMap((o) => [o.x - 8, o.r + 8]),
    ]),
  ].sort((a, b) => a - b);
  const ys = [
    ...new Set([
      s.y,
      t.y,
      s.y - 8,
      s.y + 8,
      t.y - 8,
      t.y + 8,
      ...obstacles.flatMap((o) => [o.y - 8, o.b + 8]),
      Math.min(...obstacles.map((o) => o.y)) -
        25 -
        Math.abs(e.routeOffset || 0),
    ]),
  ].sort((a, b) => a - b);
  if (e.routeOffset) {
    const y = ys[0];
    return compact([start, s, { x: s.x, y }, { x: t.x, y }, t, end]);
  }
  const startKey = `${xs.indexOf(s.x)},${ys.indexOf(s.y)}`,
    goal = `${xs.indexOf(t.x)},${ys.indexOf(t.y)}`;
  const open = [
      {
        key: startKey,
        x: xs.indexOf(s.x),
        y: ys.indexOf(s.y),
        cost: 0,
        score: 0,
        dir: "",
      },
    ],
    costs = new Map([[startKey, 0]]),
    parent = new Map(),
    closed = new Set();
  let found = false,
    iterations = 0;
  while (open.length && iterations++ < 8000) {
    let best = 0;
    for (let i = 1; i < open.length; i++)
      if (open[i].score < open[best].score) best = i;
    const n = open.splice(best, 1)[0];
    if (closed.has(n.key)) continue;
    closed.add(n.key);
    if (n.key === goal) {
      found = true;
      break;
    }
    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ]) {
      const x = n.x + dx,
        y = n.y + dy;
      if (x < 0 || y < 0 || x >= xs.length || y >= ys.length) continue;
      const key = `${x},${y}`,
        a = { x: xs[n.x], y: ys[n.y] },
        b = { x: xs[x], y: ys[y] };
      if (blocked(a, b)) continue;
      const dir = dx ? "h" : "v",
        cost =
          n.cost +
          Math.abs(a.x - b.x) +
          Math.abs(a.y - b.y) +
          (n.dir && n.dir !== dir ? 16 : 0) +
          (overlaps(a, b) ? 10000 : 0);
      if (cost < (costs.get(key) ?? Infinity)) {
        costs.set(key, cost);
        parent.set(key, n.key);
        open.push({
          key,
          x,
          y,
          cost,
          dir,
          score: cost + Math.abs(b.x - t.x) + Math.abs(b.y - t.y),
        });
      }
    }
  }
  if (found) {
    const path = [];
    let key = goal;
    while (key) {
      const [x, y] = key.split(",").map(Number);
      path.push({ x: xs[x], y: ys[y] });
      key = parent.get(key);
    }
    return compact([start, ...path.reverse(), end]);
  }
  const y = ys[0];
  return compact([start, s, { x: s.x, y }, { x: t.x, y }, t, end]);
}
export function routeConnections(project) {
  const occupied = [],
    routes = [];
  // Terminal stubs reserve their short labeled approach before long nets route.
  const ordered = [...project.connections].sort(
    (a, b) => Number(!!b.from.input) - Number(!!a.from.input),
  );
  for (const e of ordered) {
    const points = wirePoints(project, e, occupied),
      key = sourceKey(e.from);
    for (let i = 1; i < points.length; i++)
      occupied.push({ a: points[i - 1], b: points[i], key });
    routes.push({ e, points });
  }
  return routes;
}
export const pathFromPoints = (pts) =>
  pts.map((p, i) => `${i ? "L" : "M"} ${p.x} ${p.y}`).join(" ");
